import { useCallback, useMemo } from 'react'
import { arMessages } from './messages/ar'
import { enMessages, type Messages } from './messages/en'

export type Locale = 'en' | 'ar'
export type TranslationFn = (key: string, params?: Record<string, string>) => string

const catalogs: Record<Locale, Messages> = {
  en: enMessages,
  ar: arMessages,
}

function resolvePath(messages: Messages, key: string): string | undefined {
  const parts = key.split('.')
  let current: unknown = messages
  for (const part of parts) {
    if (current === null || typeof current !== 'object' || !(part in current)) {
      return undefined
    }
    current = (current as Record<string, unknown>)[part]
  }
  return typeof current === 'string' ? current : undefined
}

function interpolate(template: string, params?: Record<string, string>): string {
  if (!params) return template
  return Object.entries(params).reduce(
    (result, [name, value]) => result.replaceAll(`{{${name}}}`, value),
    template,
  )
}

function detectLocale(): Locale {
  if (typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('ar')) {
    return 'ar'
  }
  return 'en'
}

/** Lightweight i18n until a full library is adopted. Defaults to browser locale. */
export function useTranslation(locale?: Locale) {
  const activeLocale = locale ?? detectLocale()
  const messages = catalogs[activeLocale] ?? enMessages

  const t = useCallback<TranslationFn>(
    (key, params) => {
      const value = resolvePath(messages, key) ?? resolvePath(enMessages, key) ?? key
      return interpolate(value, params)
    },
    [messages],
  )

  return useMemo(() => ({ t, locale: activeLocale }), [t, activeLocale])
}
